import React, {
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InputField,
  Loader,
  MenuMulti,
  type MenuMultiOption,
  Typography,
} from '@saviynt/design-system';
import { Cookies } from 'react-cookie';

import useGetRequestableUsers from '../../../hooks/use-getrequestableusers';
import { useDebounce, useInvertedLocalize } from '../../../utils';
import {
  DEBOUNCE_DELAY,
  SKELETON_COUNT,
  USERS_PER_PAGE,
} from '../../../utils/constants';
import { type User } from '../types';

import UsersSortableList from './UsersSortableList/UsersSortableList';
import invertedMessages from './messages';

import './UsersList.css';

const INITIAL_STATUS_OPTIONS: MenuMultiOption[] = [
  { label: 'Active', value: '1', isChecked: false, isDisabled: false },
  { label: 'Inactive', value: '0', isChecked: false, isDisabled: false },
];

function UsersList({ actionsMenu }: { actionsMenu?: ReactNode }) {
  const [offset, setOffset] = useState(0);
  const [allRequestableUsers, setAllRequestableUsers] = useState<User[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [lastElement, setLastElement] = useState<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [overflowValue, setOverflowValue] = useState<'visible' | 'hidden'>(
    'hidden'
  );
  const [statusOptions, setStatusOptions] = useState<MenuMultiOption[]>(
    INITIAL_STATUS_OPTIONS
  );
  const debouncedValue = useDebounce(searchText, DEBOUNCE_DELAY);
  const l = useInvertedLocalize(invertedMessages);
  const cookies = new Cookies();
  const userName = cookies.get('user_name') as string;

  // Only pass statuskey when exactly one option is selected (API accepts a single value)
  const checkedOptions = statusOptions.filter((o) => o.isChecked);
  const statusKeyParam =
    checkedOptions.length === 1 ? Number(checkedOptions[0].value) : undefined;

  const {
    data: requestableUsersData,
    isLoading: isLoadingRequestableUsers,
    isFetching: isFetchingRequestableUsers,
    isFetched: isFetchedRequestableUsers,
  } = useGetRequestableUsers({
    requestor: userName || 'admin',
    max: USERS_PER_PAGE,
    offset,
    sort: 'username',
    order: 'asc',
    ...(debouncedValue && { sSearch: debouncedValue }),
    ...(statusKeyParam !== undefined && { statuskey: statusKeyParam }),
  });

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const firstEntry = entries[0];

      if (firstEntry.isIntersecting && hasMore) {
        setOffset((prevOffset) => prevOffset + USERS_PER_PAGE);
      }
    })
  );

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  useEffect(() => {
    setOffset(0);
    setAllRequestableUsers([]);
    setHasMore(true);
  }, [debouncedValue, statusKeyParam]);

  useEffect(() => {
    if (isFetchedRequestableUsers) {
      if (offset === 0) {
        setAllRequestableUsers(requestableUsersData?.users ?? []);
      } else {
        setAllRequestableUsers((oldUsers) => [
          ...oldUsers,
          ...(requestableUsersData?.users ?? []),
        ]);
      }

      setHasMore((requestableUsersData?.users.length ?? 0) === USERS_PER_PAGE);
    }
  }, [isFetchedRequestableUsers, requestableUsersData, offset]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (isFilterVisible) {
      timeoutId = setTimeout(() => setOverflowValue('visible'), 200);
    } else {
      setOverflowValue('hidden');
    }

    return () => clearTimeout(timeoutId);
  }, [isFilterVisible]);

  const isFilterApplied = statusOptions.some((o) => o.isChecked);

  const skeletonCount = useMemo(() => {
    if (isLoadingRequestableUsers && offset === 0) {
      return USERS_PER_PAGE;
    }

    if (isFetchingRequestableUsers && offset > 0) {
      return Math.min(USERS_PER_PAGE, SKELETON_COUNT);
    }

    return 0;
  }, [isLoadingRequestableUsers, isFetchingRequestableUsers, offset]);

  return (
    <Box className='Ext-UsersList'>
      {allRequestableUsers.length > 0 || searchText.length > 0 ? (
        <Box className='Ext-UsersList-toolbar'>
          <Box className='Ext-UsersList-searchAndFilter'>
            <Box className='Ext-UsersList-searchAndFilterLeft'>
              <InputField
                name='basic'
                size='medium'
                placeholder={l('Search identities')}
                kind='search'
                prefixIcon={
                  <Icon kind='Search' color='neutral-600' size='smallMedium' />
                }
                value={searchText}
                setValue={setSearchText}
                classNames={{ container: 'Ext-UsersList-input' }}
              />
              <ButtonSelect
                label={l('Filter')}
                size='medium'
                onClick={() => setIsFilterVisible((v) => !v)}
                isOpen={isFilterVisible}
                BadgeComp={
                  isFilterApplied ? (
                    <Badge kind='dot' color='primary' size='small' />
                  ) : null
                }
                prefixIcon={<Icon kind='Filter' />}
              />
            </Box>
            {actionsMenu}
          </Box>
          <Box
            style={{ overflow: overflowValue }}
            className={`Ext-UsersList-filtersWrapper${
              isFilterVisible ? ' Ext-UsersList-filtersWrapper--isVisible' : ''
            }`}>
            <MenuMulti
              label={l('Status')}
              options={statusOptions}
              onChange={(selected) => {
                setStatusOptions((prev) =>
                  prev.map((opt) => ({
                    ...opt,
                    isChecked: (selected ?? []).some(
                      (s) => s.value === opt.value
                    ),
                  }))
                );
              }}
              trigger={
                <ButtonSelect
                  kind='pill'
                  label=''
                  ariaLabel={l('Status filter')}
                />
              }
              minOptionsForFooter={0}
            />
          </Box>
        </Box>
      ) : null}

      {isLoadingRequestableUsers &&
      offset === 0 &&
      allRequestableUsers.length === 0 ? (
        <Box className='Ext-UsersList-loader'>
          <Loader kind='loop' format='main' color='brand' size='large' />
        </Box>
      ) : (
        <>
          {allRequestableUsers.length > 0 && (
            <UsersSortableList
              allUsers={allRequestableUsers}
              skeletonCount={skeletonCount}
              hasMore={hasMore}
              isLoadingRequestableUsers={isLoadingRequestableUsers}
              isFetchingRequestableUsers={isFetchingRequestableUsers}
              setLastElement={setLastElement}
              offset={offset}
            />
          )}

          {!isLoadingRequestableUsers &&
          allRequestableUsers.length < 1 &&
          searchText ? (
            <Typography kind='body1'>{l('No users found')}</Typography>
          ) : null}
        </>
      )}
    </Box>
  );
}

UsersList.defaultProps = { actionsMenu: undefined };

export default UsersList;
